/**
 * ConversionConfirm — Paket B (premium-checkout-redesign)
 *
 * Karten-Tab von /checkout im mode 'conversion' (Krypto-/Legacy-Premium ohne
 * laufendes Stripe-Abo): keine Zahlung, die Restlaufzeit wird pro rata in
 * den Zieltarif umgerechnet (PATCH /api/v2/subscription). Ausgelagert aus
 * CardPaymentPane, damit diese Datei nicht zu gross wird.
 */
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatSubscriptionDate } from '../subscription/subscriptionUtils'

type ConversionConfirmProps = {
  planName: string
  duration: 1 | 3 | 6 | 12
  daysRemaining: number | undefined
  conversionExpiresAt: string | null
  changePlan: (planId: string, durationMonths: number) => Promise<void>
  planId: string
  onSuccess: () => void
}

export function ConversionConfirm({
  planName,
  duration,
  daysRemaining,
  conversionExpiresAt,
  changePlan,
  planId,
  onSuccess,
}: ConversionConfirmProps) {
  const { t } = useTranslation('checkout')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      await changePlan(planId, duration)
      onSuccess()
    } catch {
      setError(t('card.paymentError'))
    } finally {
      setIsLoading(false)
    }
  }, [changePlan, planId, duration, onSuccess, t])

  return (
    <div data-testid="card-conversion-confirm">
      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <p className="muted" style={{ fontSize: 13, margin: 0 }}>
          {t('card.conversionText', {
            days: Math.round(daysRemaining ?? 0),
            plan: planName,
            date: conversionExpiresAt ? formatSubscriptionDate(conversionExpiresAt) : '—',
          })}
        </p>
      </div>
      {error && (
        <p role="alert" style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary btn-lg"
        style={{ width: '100%' }}
        disabled={isLoading}
        onClick={() => { void handleConfirm() }}
        data-testid="confirm-conversion-btn"
      >
        {isLoading ? t('card.confirmPlanChangeLoading') : t('card.conversionCta')}
      </button>
      <p className="muted" style={{ fontSize: 11, textAlign: 'center', margin: '10px 0 0' }}>
        {t('card.conversionHntHint')}
      </p>
    </div>
  )
}
